import React, { useState } from 'react';
import { Lock, ShieldCheck } from 'lucide-react';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { toast } from '../ui/use-toast';
import { jwtAuthService } from '../../services/jwtAuthService';

interface ForcePasswordChangeProps {
  onPasswordChanged: () => void;
}

export const ForcePasswordChange: React.FC<ForcePasswordChangeProps> = ({ onPasswordChanged }) => {
  const [loading, setLoading] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!currentPassword) {
      newErrors.current_password = 'Please enter your current password';
    }
    if (!newPassword) {
      newErrors.new_password = 'Please enter a new password';
    } else if (newPassword.length < 8) {
      newErrors.new_password = 'Password must be at least 8 characters';
    } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/.test(newPassword)) {
      newErrors.new_password = 'Password must contain uppercase, lowercase, number, and special character';
    }
    if (!confirmPassword) {
      newErrors.confirm_password = 'Please confirm your password';
    } else if (newPassword !== confirmPassword) {
      newErrors.confirm_password = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const user = jwtAuthService.getCurrentUser();
      if (!user) {
        toast({ title: 'User session not found. Please log in again.', variant: 'destructive' });
        return;
      }

      const hostname = window.location.hostname;
      const protocol = window.location.protocol;
      const apiUrl = (hostname.includes('localhost') || hostname.includes('127.0.0.1'))
        ? `${protocol}//localhost:8000`
        : `${protocol}//${hostname}`;

      const response = await fetch(`${apiUrl}/v1/platform/users/change-password/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtAuthService.getAccessToken()}`,
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to change password');
      }

      toast({ title: 'Password changed successfully!', variant: 'success' });

      const updatedUser = { ...user, force_password_change: false };
      localStorage.setItem('jwt_user', JSON.stringify(updatedUser));

      onPasswordChanged();
    } catch (error: any) {
      console.error('Password change error:', error);
      toast({ title: error.message || 'Failed to change password. Please try again.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary to-primary-700 p-5">
      <Card className="max-w-[500px] w-full shadow-2xl rounded-2xl">
        <CardHeader className="text-center pb-2">
          <div className="inline-flex items-center justify-center mx-auto p-4 bg-gradient-to-br from-primary to-primary-700 rounded-full mb-4">
            <ShieldCheck className="h-10 w-10 text-primary-foreground" />
          </div>
          <h3 className="text-xl font-semibold">Password Change Required</h3>
          <p className="text-sm text-muted-foreground mt-1">
            For security reasons, you must change your password before proceeding
          </p>
        </CardHeader>
        <CardContent>
          <Alert className="mb-6 border-warning bg-warning-100 text-warning-700">
            <AlertTitle className="font-semibold">First Time Login Security</AlertTitle>
            <AlertDescription>
              You're logging in with a temporary password. Please create a new, secure password to protect your account.
            </AlertDescription>
          </Alert>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current_password">Current Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="current_password"
                  type="password"
                  placeholder="Enter the temporary password from your email"
                  className="pl-10"
                  value={currentPassword}
                  onChange={(e) => { setCurrentPassword(e.target.value); setErrors((prev) => ({ ...prev, current_password: '' })); }}
                />
              </div>
              {errors.current_password && <p className="text-sm text-destructive">{errors.current_password}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="new_password">New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="new_password"
                  type="password"
                  placeholder="Create a strong password"
                  className="pl-10"
                  value={newPassword}
                  onChange={(e) => { setNewPassword(e.target.value); setErrors((prev) => ({ ...prev, new_password: '' })); }}
                />
              </div>
              {errors.new_password && <p className="text-sm text-destructive">{errors.new_password}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm_password">Confirm New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="confirm_password"
                  type="password"
                  placeholder="Re-enter your new password"
                  className="pl-10"
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); setErrors((prev) => ({ ...prev, confirm_password: '' })); }}
                />
              </div>
              {errors.confirm_password && <p className="text-sm text-destructive">{errors.confirm_password}</p>}
            </div>

            <Button type="submit" className="w-full" size="lg" disabled={loading}>
              <ShieldCheck className="mr-2 h-4 w-4" />
              {loading ? 'Changing Password...' : 'Change Password & Continue'}
            </Button>
          </form>

          <div className="mt-6 p-4 bg-muted rounded-lg">
            <p className="font-semibold text-sm mb-2">Password Requirements:</p>
            <ul className="space-y-1 text-xs text-muted-foreground">
              <li>• At least 8 characters long</li>
              <li>• Contains uppercase and lowercase letters</li>
              <li>• Contains at least one number</li>
              <li>• Contains at least one special character (@$!%*?&amp;)</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForcePasswordChange;
